import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { MessageSquare, X, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkContactBar = ({ 
  selectedItems = [], // Array of {id, name, role, companyId, companyName}
  onClearSelection,
  itemType = "resources" // "resources" or "projects"
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null);

  // Default templates
  const getDefaultTemplate = () => {
    if (itemType === "resources") {
      return `Hello,

I am interested in learning more about {resourceName}, a {role} from your company.

Could we discuss availability and rates for this profile?

Best regards`;
    } else {
      return `Hello,

I would like to discuss one of our consultants for your project "{projectTitle}".

I believe we have a great match for your requirements.

Best regards`;
    }
  };

  const handleOpenDialog = () => {
    setMessageTemplate(getDefaultTemplate());
    setResult(null);
    setShowDialog(true);
  };

  const handleSendAll = async () => {
    setIsSending(true);
    const token = localStorage.getItem('token');
    
    const results = {
      success: [],
      failed: [],
      total: selectedItems.length
    };

    for (const item of selectedItems) {
      try {
        // Personalize message
        let personalizedMessage = messageTemplate
          .replace(/{resourceName}/g, item.name || 'this resource')
          .replace(/{role}/g, item.role || 'consultant')
          .replace(/{projectTitle}/g, item.title || 'this project')
          .replace(/{companyName}/g, item.companyName || 'your company');

        // Generate subject
        const subject = itemType === "resources" 
          ? `Inquiry about ${item.name || 'consultant'}`
          : `Proposal for ${item.title || 'project'}`;

        // Send message
        await axios.post(`${API}/messages`, {
          to_company_id: item.companyId,
          subject,
          content: personalizedMessage,
          resource_id: itemType === "resources" ? item.id : null,
          message_type: "user"
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        results.success.push(item);
      } catch (error) {
        console.error(`Failed to send message for ${item.name}:`, error);
        results.failed.push({
          item,
          error: error.response?.data?.detail || error.message
        });
      }
    }

    setResult({
      ...results,
      success_count: results.success.length,
      failed_count: results.failed.length
    });
    setIsSending(false);

    if (results.failed.length === 0) {
      toast.success(`Successfully contacted ${results.success_count} ${itemType}!`);
      setTimeout(() => {
        setShowDialog(false);
        onClearSelection();
      }, 2000);
    }
  };

  if (selectedItems.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-slide-up">
        <div 
          className="bg-white shadow-2xl rounded-lg border-2 border-blue-500 px-6 py-4 flex items-center gap-4"
          style={{ minWidth: '500px' }}
        >
          <div className="flex-1">
            <p className="text-lg font-semibold" style={{ color: '#1B3240' }}>
              {selectedItems.length} {itemType} selected
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Ready to contact multiple {itemType} at once
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleOpenDialog}
              style={{ backgroundColor: '#62D84E' }}
              className="text-white"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact All
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={onClearSelection}
            >
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {result ? 'Message Results' : `Contact ${selectedItems.length} ${itemType}`}
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  📝 Personalized Messages
                </p>
                <p className="text-xs text-blue-700">
                  Each {itemType === "resources" ? "resource" : "project"} will receive a separate, personalized conversation.
                  Available variables: <code className="bg-blue-100 px-1 rounded">{`{resourceName}`}</code>, 
                  <code className="bg-blue-100 px-1 ml-1 rounded">{`{role}`}</code>, 
                  <code className="bg-blue-100 px-1 ml-1 rounded">{`{companyName}`}</code>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Template
                </label>
                <Textarea
                  value={messageTemplate}
                  onChange={(e) => setMessageTemplate(e.target.value)}
                  placeholder="Type your message..."
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Preview: {selectedItems.length} separate conversations will be created
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {result.success_count > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      ✅ Successfully contacted {result.success_count} {itemType}
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      {result.success_count} separate conversations created
                    </p>
                  </div>
                </div>
              )}

              {result.failed_count > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-2">
                    ❌ Failed to contact {result.failed_count} {itemType}
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {result.failed.map((failure, index) => (
                      <div key={index} className="text-xs text-red-700 py-1 border-t border-red-200">
                        {failure.item.name} - {failure.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!result ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setShowDialog(false)} 
                  disabled={isSending}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendAll} 
                  disabled={isSending || !messageTemplate.trim()}
                  style={{ backgroundColor: '#62D84E' }}
                  className="text-white"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Send to {selectedItems.length} {itemType}
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={() => {
                setShowDialog(false);
                if (result.failed_count === 0) {
                  onClearSelection();
                }
              }}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkContactBar;
